import { ref } from 'vue'

type FetchOptions = NonNullable<Parameters<typeof $fetch>[1]>

/**
 * Imperative API helper shared by pages, components and stores.
 *
 * It intentionally does not call `useFetch`: that composable belongs to the
 * synchronous part of `setup()`, while this helper is awaited from nested
 * async functions (loaders, submit handlers). The Nuxt instance is resolved
 * synchronously on entry; during SSR it stays reachable after nested awaits
 * because `experimental.asyncContext` is enabled in `nuxt.config.ts`.
 */
export async function mainFetch(uri: string, opts: FetchOptions = {}) {
  const api = useNuxtApp().$mainFetch
  try {
    const data = await api(uri, opts as never)
    return { data: ref<any>(data), error: ref<any>(null) }
  } catch (error: any) {
    return { data: ref<any>(null), error: ref<any>(error) }
  }
}

/**
 * Localized message of a failed API call (`error.clientMessage` of the
 * ErrorBody), or `fallback` when the API sent none.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const message = apiErrorBody(error)?.clientMessage
  return typeof message === 'string' && message !== '' ? message : fallback
}

/** `error` of the API ErrorBody `{ error: { code, message, clientMessage?, details?, requestId } }`. */
export function apiErrorBody(error: unknown): { code?: string; message?: string; clientMessage?: string; details?: any } | null {
  const body = (error as { data?: { error?: unknown } } | null | undefined)?.data?.error
  return typeof body === 'object' && body !== null ? body : null
}
